import { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { TransitionGroup } from 'react-transition-group';
import { Drawer, Fade, Typography, alpha, makeStyles } from '@material-ui/core';
import ActionButton from '../actionButton';
import Search from '../search';
import CustomScrollBar from '../customScrollBar';
import TeamUserStatsCard from '../teamUserStatsCard';
import NotificationCard from '../notificationCard';
import { isArrayEmpty, trimString } from '../../../utility/helpers';
import { customSearch } from '../../../utility/uiUtils';
import { ACTION_BUTTON_TYPES } from '../actionButton/config';

const useStyles = makeStyles(
  ({ palette: { primary }, spacing, breakpoints }) => ({
    root: {
      boxSizing: 'border-box',
      backgroundColor: primary.bluish9,
      padding: spacing(6),
      width: '100vw',
      maxWidth: 1137,
      [breakpoints.up('xl')]: {
        maxWidth: 1579,
      },
    },
    header: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginBottom: spacing(4),
    },
    search: {
      maxWidth: '100%',
      paddingBottom: spacing(4),
    },
    content: {
      display: 'flex',
      flexDirection: 'column',
      height: 'calc(100% - 102px)',
    },
    teamWrapper: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit,205px)',
      gridGap: 16,
      width: '100%',
      paddingTop: spacing(3),
    },
    teamWrapperEmpty: {
      paddingTop: 0,
    },
    teamItemWrapper: {
      padding: spacing(0, 0.5),
    },
    team: {
      boxShadow: `0px 2px 8px 0px ${alpha(primary.black, 0.07)}`,
    },
    scrollY: {
      top: 12,
      right: -15,
      height: 'calc(100% - 12px)',
    },
  })
);

const PreviewTeamDialog = ({
  translations,
  title,
  isOpened,
  team = [],
  courseStatuses,
  hasCourses = false,
  canAccessUserProfileCheck,
  canAccessProfileSectionCheck,
  onGoToUserProfile,
  onClose,
}) => {
  const classes = useStyles();

  const [search, setSearch] = useState('');

  const teamMembers = useMemo(
    () => customSearch(team, trimString(search), true),
    [search, team]
  );

  const onSearch = value => setSearch(value);

  const handleClose = () => {
    setSearch('');
    onClose();
  };

  return (
    <Drawer
      anchor="right"
      classes={{
        paper: classes.root,
      }}
      open={isOpened}
      onClose={handleClose}
    >
      <div className={classes.header}>
        <Typography className={classes.title} variant="h5">
          {title}
        </Typography>
        <ActionButton
          type={ACTION_BUTTON_TYPES.CLOSE}
          onClickHandler={handleClose}
        />
      </div>
      <Search
        className={classes.search}
        placeholder={translations.placeholder}
        onChange={onSearch}
      />
      <div className={classes.content}>
        <CustomScrollBar
          customScrollBarYClass={classes.scrollY}
          verticalScroll
          removeScrollX
          passContentHeight
          passContentWidth
        >
          <TransitionGroup
            className={classNames(classes.teamWrapper, {
              [classes.teamWrapperEmpty]: isArrayEmpty(teamMembers),
            })}
          >
            {teamMembers.map(teamMember => (
              <Fade key={`team_member_${teamMember.id}`} in unmountOnExit>
                <div className={classes.teamItemWrapper}>
                  <TeamUserStatsCard
                    className={classes.team}
                    translations={translations}
                    user={teamMember}
                    courseStatuses={courseStatuses}
                    canViewCourses={hasCourses}
                    canAccessUserProfileCheck={canAccessUserProfileCheck}
                    canAccessProfileSectionCheck={canAccessProfileSectionCheck}
                    onGoToUserProfile={onGoToUserProfile}
                  />
                </div>
              </Fade>
            ))}
          </TransitionGroup>
        </CustomScrollBar>
        <NotificationCard
          content={translations.noResults}
          shouldFade={!!trimString(search) && isArrayEmpty(teamMembers)}
        />
      </div>
    </Drawer>
  );
};

PreviewTeamDialog.propTypes = {
  translations: PropTypes.object.isRequired,
  title: PropTypes.string.isRequired,
  team: PropTypes.arrayOf(PropTypes.object),
  courseStatuses: PropTypes.arrayOf(PropTypes.object).isRequired,
  hasCourses: PropTypes.bool,
  canAccessUserProfileCheck: PropTypes.func.isRequired,
  canAccessProfileSectionCheck: PropTypes.func.isRequired,
  onGoToUserProfile: PropTypes.func.isRequired,
};

export default PreviewTeamDialog;
